import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Trash2, Plus, Image as ImageIcon, Video, Lock, Upload, Loader2, ChevronUp, ChevronDown, GripVertical, Edit2, Check, X, Clock } from 'lucide-react';
import axios from 'axios';
import ConnectedDisplays from '../components/ConnectedDisplays';
import WeatherZipConfig from '../components/WeatherZipConfig';
import SlideTiming from '../components/SlideTiming';
import FullyKioskControl from '../components/FullyKioskControl';
import AlertConfig from '../components/AlertConfig';
import LivePreview from '../components/LivePreview';
import ZoneConfig from '../components/ZoneConfig';
import WhatsNew from '../components/WhatsNew';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getYouTubeEmbedUrl = (url) => {
  if (!url) return url;
  if (url.includes('/embed/')) return url;
  
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];
  const vMatch = url.match(/youtube\.com\/v\/([^?&]+)/);
  if (vMatch) videoId = vMatch[1];
  
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url;
};

const PcocAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [content, setContent] = useState([]);
  const [newItemUrl, setNewItemUrl] = useState('');
  const [newItemType, setNewItemType] = useState('image');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [uploadMode, setUploadMode] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  
  const ADMIN_PASSWORD = 'pcoc2026';
  
  useEffect(() => {
    const auth = sessionStorage.getItem('pcocAdminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      loadContent();
    }
  }, []);
  
  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pcoc-content`);
      setContent(response.data.items || []);
    } catch (error) {
      console.error('Failed to load content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('pcocAdminAuth', 'true');
      loadContent();
      toast.success('Access granted!');
    } else {
      toast.error('Incorrect password');
      setPassword('');
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('pcocAdminAuth');
    setPassword('');
    toast.success('Logged out');
  };
  
  const saveContent = async (newContent) => {
    setSaving(true);
    try {
      await axios.put(`${API}/pcoc-content`, { items: newContent });
      setContent(newContent);
      toast.success('Content saved to database!');
    } catch (error) {
      console.error('Failed to save content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 1024 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 1GB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.success) {
        const newItem = {
          type: response.data.type,
          url: `${BACKEND_URL}${response.data.url}`,
        };
        const updated = [...content, newItem];
        saveContent(updated);
        toast.success(`${response.data.type} uploaded successfully!`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  
  const handleAddItem = () => {
    if (!newItemUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    
    const processedUrl = newItemType === 'video' 
      ? getYouTubeEmbedUrl(newItemUrl.trim())
      : newItemUrl.trim();
    
    const newItem = { type: newItemType, url: processedUrl, description: newItemDescription.trim() || '' };
    const updated = [...content, newItem];
    saveContent(updated);
    setNewItemUrl('');
    setNewItemDescription('');
    toast.success(`${newItemType === 'image' ? 'Image' : 'Video'} added!`);
  };
  
  const handleRemoveItem = (index) => {
    const updated = content.filter((_, i) => i !== index);
    saveContent(updated);
    toast.success('Item removed');
  };
  
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updated = [...content];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    saveContent(updated);
  };
  
  const handleMoveDown = (index) => {
    if (index === content.length - 1) return;
    const updated = [...content];
    [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
    saveContent(updated);
  };
  
  const startEditDescription = (index) => {
    setEditingIndex(index);
    setEditDescription(content[index].description || '');
  };
  
  const saveDescriptionEdit = (index) => {
    const updated = [...content];
    updated[index] = { ...updated[index], description: editDescription };
    saveContent(updated);
    setEditingIndex(null);
    setEditDescription('');
  };
  
  const cancelEditDescription = () => {
    setEditingIndex(null);
    setEditDescription('');
  };
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-cyan-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">PCOC Admin Panel <span className="text-cyan-400 text-sm font-normal">Snowman 1</span></h1>
              <p className="text-slate-400">Enter password to continue</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
                autoFocus
              />
              <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600 text-white">
                Login
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-slate-800 text-center">
              <a href="/" className="text-slate-400 hover:text-cyan-400 text-sm">Back to Home</a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4">
      <WhatsNew />
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              PCOC Page <span className="text-cyan-400">Admin</span>
            </h1>
            <p className="text-slate-400">Manage images and videos for /pcoc page</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
            Logout
          </Button>
        </div>
        
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4">Add New Content</h3>
            
            <div className="flex gap-2 mb-4">
              <Button
                onClick={() => setUploadMode('url')}
                variant={uploadMode === 'url' ? 'default' : 'outline'}
                className={uploadMode === 'url' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                size="sm"
              >
                Enter URL
              </Button>
              <Button
                onClick={() => setUploadMode('file')}
                variant={uploadMode === 'file' ? 'default' : 'outline'}
                className={uploadMode === 'file' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                size="sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload File
              </Button>
            </div>
            
            {uploadMode === 'url' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      type="text"
                      value={newItemUrl}
                      onChange={(e) => setNewItemUrl(e.target.value)}
                      placeholder="Enter image URL or YouTube embed URL"
                      className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
                    />
                    <p className="text-slate-500 text-xs mt-1">
                      Image: Direct URL | Video: YouTube embed URL (https://www.youtube.com/embed/...)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={newItemType}
                      onChange={(e) => setNewItemType(e.target.value)}
                      className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 flex-1"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Input
                    type="text"
                    value={newItemDescription}
                    onChange={(e) => setNewItemDescription(e.target.value)}
                    placeholder="Description (optional) - e.g., 'Holiday promo', 'Menu board'"
                    className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 flex-1"
                  />
                  <Button onClick={handleAddItem} className="bg-cyan-500 hover:bg-cyan-600 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center hover:border-cyan-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                    <Upload className={`w-12 h-12 mb-4 ${uploading ? 'text-cyan-400 animate-pulse' : 'text-slate-400'}`} />
                    <p className="text-white font-medium mb-1">
                      {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-slate-500 text-sm">
                      Images: JPG, PNG, GIF, WEBP | Videos: MP4, WEBM, MOV (Max 1GB)
                    </p>
                  </label>
                </div>
                {uploading && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                    <p className="text-cyan-400 text-sm text-center">Uploading file...</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4">Current Content ({content.length} items)</h3>
            
            {content.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-400">No content added yet</p>
                <p className="text-slate-500 text-sm mt-2">Add images or videos above to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {content.map((item, index) => (
                  <div key={index} className="bg-slate-950 p-4 rounded-lg border border-slate-800 flex items-start gap-4">
                    <div className="flex-shrink-0 flex flex-col items-center gap-1 pt-2">
                      <span className="text-slate-500 text-xs font-medium">#{index + 1}</span>
                      <GripVertical className="w-4 h-4 text-slate-600" />
                    </div>
                    
                    <div className="flex-shrink-0 w-24 h-16 bg-slate-800 rounded overflow-hidden">
                      {item.type === 'image' ? (
                        <img src={item.url} alt={item.description || `Image ${index + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Video className="w-8 h-8 text-red-400" /></div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {item.type === 'image' ? <ImageIcon className="w-4 h-4 text-cyan-400" /> : <Video className="w-4 h-4 text-red-400" />}
                        <span className="text-white text-sm font-medium">{item.type === 'image' ? 'Image' : 'Video'}</span>
                      </div>
                      
                      {editingIndex === index ? (
                        <div className="flex items-center gap-2 mt-2">
                          <Input type="text" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Enter description..." className="bg-slate-900 border-slate-700 text-white text-sm h-8 flex-1" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') saveDescriptionEdit(index); if (e.key === 'Escape') cancelEditDescription(); }} />
                          <Button size="sm" onClick={() => saveDescriptionEdit(index)} className="h-8 w-8 p-0 bg-green-500 hover:bg-green-600"><Check className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={cancelEditDescription} className="h-8 w-8 p-0 border-slate-700"><X className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1 cursor-pointer group" onClick={() => startEditDescription(index)}>
                          {item.description ? <p className="text-slate-300 text-sm">{item.description}</p> : <p className="text-slate-500 text-sm italic">No description</p>}
                          <Edit2 className="w-3 h-3 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                      
                      <p className="text-slate-500 text-xs truncate mt-1">{item.url}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <select
                          value={item.duration || ''}
                          onChange={(e) => {
                            const updated = [...content];
                            updated[index] = { ...updated[index], duration: e.target.value ? parseInt(e.target.value) : undefined };
                            saveContent(updated);
                          }}
                          className="bg-slate-900 border border-slate-700 text-white rounded text-xs px-2 py-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">Default</option>
                          <option value="3">3s</option>
                          <option value="5">5s</option>
                          <option value="10">10s</option>
                          <option value="15">15s</option>
                          <option value="20">20s</option>
                          <option value="30">30s</option>
                          <option value="45">45s</option>
                          <option value="60">60s</option>
                          <option value="90">90s</option>
                          <option value="120">2m</option>
                        </select>
                        {item.duration && <span className="text-cyan-400 text-xs">{item.duration}s</span>}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <Button onClick={() => handleMoveUp(index)} disabled={index === 0} size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30 h-8 w-8 p-0"><ChevronUp className="w-4 h-4" /></Button>
                      <Button onClick={() => handleMoveDown(index)} disabled={index === content.length - 1} size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30 h-8 w-8 p-0"><ChevronDown className="w-4 h-4" /></Button>
                      <Button onClick={() => handleRemoveItem(index)} size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Weather Zip Code Config */}
        <div className="mt-8">
          <WeatherZipConfig pageId="pcoc" />
        </div>
        
        {/* Slide Timing */}
        <div className="mt-8">
          <SlideTiming pageId="pcoc" />
        </div>
        
        {/* Connected Displays */}
        <div className="mt-8">
          <ConnectedDisplays pageId="pcoc" />
        </div>
        
        {/* Fully Kiosk Remote Control */}
        <div className="mt-8">
          <FullyKioskControl pageId="pcoc" />
        </div>

        {/* Announcement Bar */}
        <div className="mt-8">
          <AlertConfig pageId="pcoc" />
        </div>

        {/* Multi-Zone Layout */}
        <div className="mt-8">
          <ZoneConfig pageId="pcoc" />
        </div>

        {/* Live Preview */}
        <div className="mt-8">
          <LivePreview content={content} slideDuration={5} />
        </div>
        
        <div className="flex gap-4 justify-center mt-8">
          <a href="/pcoc" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 text-sm">
            View PCOC Page →
          </a>
          <a href="/" className="text-cyan-400 hover:text-cyan-300 text-sm">
            Back to Home →
          </a>
        </div>
      </div>
    </div>
  );
};

export default PcocAdmin;
